import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import {
    FeatureField,
    ImageSignatureFeatureField
} from 'src/app/feature/map-viewer/side-panel/feature-panel/feature-fields/feature-field';
import { TemplatedFeature } from 'src/app/feature/map-viewer/side-panel/feature-panel/feature-fields/templated-feature';
import { FeaturePanelUtil } from 'src/app/feature/map-viewer/side-panel/feature-panel/feature-panel.util';
import { ButtonType } from 'src/app/shared/common/components/buttons/button';
import { ContextMenuActions } from 'src/app/shared/common/components/context-menu/gsp-context-menu.component';
import { NotificationType } from 'src/app/shared/common/components/gsp-notification/gsp-notification.component';
import { FeaturesStreamsService } from 'src/app/shared/common/current-features/features-streams.service';
import { TaskFeaturesStreamsService } from 'src/app/shared/common/current-features/task-features-streams.service';
import { TasksStreamsService } from 'src/app/shared/common/current-tasks/tasks-streams.service';
import { TemplatesStoreService } from 'src/app/shared/common/current-templates/templates-store.service';
import { TaskCreationMode } from 'src/app/shared/common/task/task';
import { Feature } from 'src/app/shared/map-data-services/feature/feature';

type PreviewFields = {
    previewField1: FeatureField;
    previewField2: FeatureField;
    thumbnailField: ImageSignatureFeatureField;
};

@Component({
    selector: 'task-forms-selection',
    templateUrl: './task-forms-selection.component.html'
})
export class TaskFormsSelectionComponent implements OnInit, OnDestroy {
    @Input()
    isSummaryMode: boolean = false;

    @Input()
    taskCreationMode: TaskCreationMode = TaskCreationMode.NEW;

    @Output()
    layerToFeaturesMap = new EventEmitter<Map<string, Feature[]>>();

    public selectedFeatures: Feature[];
    public layerIdToFeaturesMap: Map<string, Feature[]> = new Map();
    public featureIdToPreviewsMap: Map<string, PreviewFields> = new Map();
    public layerIdsInOrder: string[];
    public isFeatureRemoved: boolean = false;
    public isNonTemplatedFeaturesExcluded: boolean = false;
    public previewFieldReminder: boolean = false;

    TaskCreationMode = TaskCreationMode;

    openAccordionIndices: number[] = [];

    public contextMenuItems: ContextMenuActions[] = [
        {
            name: this.translateService.instant('MapViewer_Generic_RemoveSelection'),
            id: 'delete-selected-form-features',
            iconClass: 'i24 icon_line_trash_black_24',
            divider: false,
            title: false,
            execute: () => this.clearAllFormSelection(),
            visible: () => true
        }
    ];

    // Expose enum to template
    public ButtonType = ButtonType;
    public NotificationType = NotificationType;

    private destroyed$ = new Subject<void>();

    constructor(
        private featuresStreams: FeaturesStreamsService,
        private tasksStreams: TasksStreamsService,
        private taskFeaturesStreams: TaskFeaturesStreamsService,
        private templatesStoreService: TemplatesStoreService,
        private translateService: TranslateService
    ) {}

    async ngOnInit() {
        this.getPreviousSelection();
        this.taskFeaturesStreams.taskSelectedFeaturesStream
            .pipe(takeUntil(this.destroyed$))
            .subscribe(selectedFeatures => {
                // Remove features without an associated template (imported features)
                const templatedFeatures = selectedFeatures.filter(
                    feature => feature.templateSeriesId || feature.templateId
                );
                this.isNonTemplatedFeaturesExcluded = selectedFeatures.length !== templatedFeatures.length;

                this.selectedFeatures = templatedFeatures;
                if (this.selectedFeatures) {
                    this.createLayerIdToFeaturesMap();
                }
                if (this.taskCreationMode !== TaskCreationMode.EDIT) {
                    this.layerToFeaturesMap.emit(this.layerIdToFeaturesMap);
                }
            });
    }

    private getPreviousSelection() {
        this.selectedFeatures = this.taskFeaturesStreams.taskSelectedFeaturesStream.getValue();
        this.createLayerIdToFeaturesMap();
        this.layerToFeaturesMap.emit(this.layerIdToFeaturesMap);
    }

    private async createLayerIdToFeaturesMap() {
        if (!this.isFeatureRemoved) {
            // Order selected features like in features-panel. Do not sort when removing features on task editmode
            this.selectedFeatures = FeaturePanelUtil.orderFeaturesInList(this.selectedFeatures);
        }

        this.layerIdToFeaturesMap = this.selectedFeatures.reduce((map, feature) => {
            if (!map.has(feature.layerId)) {
                map.set(feature.layerId, []);
            }
            map.get(feature.layerId).push(feature);
            return map;
        }, new Map<string, Feature[]>());

        this.layerIdsInOrder = [...new Set(this.selectedFeatures.map(feature => feature.layerId))];

        await Promise.all(
            Array.from(this.layerIdToFeaturesMap.entries()).map(async ([key, values]) => {
                for (const feature of values) {
                    if (feature.templateId) {
                        const template = await this.templatesStoreService.getTemplateById(feature.templateId);
                        const templatedFeature = new TemplatedFeature(feature, template);

                        const previewFields: PreviewFields = {
                            previewField1: templatedFeature.fields.find(
                                field => field.uuid === template.review.line1FieldId
                            ),
                            previewField2: templatedFeature.fields.find(
                                field => field.uuid === template.review.line2FieldId
                            ),
                            thumbnailField: templatedFeature.fields.find(
                                field => field.uuid === template.review.thumbnailFieldId
                            )
                        };

                        this.featureIdToPreviewsMap.set(feature.id, previewFields);
                    }
                }
            })
        );
    }

    private clearAllFormSelection() {
        this.featuresStreams.clearSelectedFeatures();
        this.selectedFeatures = [];
        this.layerIdsInOrder = [];
        this.layerIdToFeaturesMap = new Map();
        this.featureIdToPreviewsMap = new Map();

        this.taskFeaturesStreams.taskSelectedFeaturesStream.next([]);
    }

    public removeFormFromSelection(featureId: string, layerId: string) {
        // Remove from layerIdToFeaturesMap
        let features = this.layerIdToFeaturesMap.get(layerId);
        if (features) {
            let layerFeatureIndex = features.findIndex(feature => feature.id === featureId);
            if (layerFeatureIndex !== -1) {
                features.splice(layerFeatureIndex, 1);
            }
            if (features.length === 0) {
                this.layerIdToFeaturesMap.delete(layerId);
                this.layerIdsInOrder = this.layerIdsInOrder.filter(id => id !== layerId);
            }
        }

        this.featuresStreams.removeFromSelectedFeatures(featureId);
        this.featureIdToPreviewsMap.delete(featureId);
        this.isFeatureRemoved = true;

        this.taskFeaturesStreams.taskSelectedFeaturesStream.next(
            this.selectedFeatures.filter(feature => feature.id !== featureId)
        );
    }

    public toggleAccordion(index: number): void {
        const isOpen = this.openAccordionIndices.includes(index);

        if (isOpen) {
            this.openAccordionIndices = this.openAccordionIndices.filter(i => i !== index);
        } else {
            this.openAccordionIndices.push(index);
        }
    }

    public featuresByLayerId(layerId: string): Feature[] {
        return this.layerIdToFeaturesMap.get(layerId) || [];
    }

    public previewFieldsByFeatureId(featureId: string): PreviewFields {
        const preview = this.featureIdToPreviewsMap.get(featureId);

        if (!preview?.previewField1 && !preview?.previewField2) {
            return null;
        }

        return preview;
    }

    ngOnDestroy() {
        this.layerToFeaturesMap.emit(this.layerIdToFeaturesMap);
        this.tasksStreams.taskFeatureSelectionModeActiveStream.next(false);
        this.destroyed$.next();
    }
}
